import * as H from "history";
import * as React from "react";
import { toast } from "react-toastify";

import Table from "src/core/dashboard/components/widgets/table/Table";
import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/core/shared/api";
import { getAdminCreditRequestTicketsColumns } from "src/core/shared/components/tables";
import { Button } from "src/library/components/Button";
import Grid from "src/library/components/Hedron";
import { ITableInstance } from "src/library/components/Tables/Table";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import {
  CreditRequestStatus,
  useCreditRequestApi
} from "src/modules/credit-request/api/credit-request.api";
import {
  CreditStatus,
  ISupportTicket
} from "src/modules/support/api/support.api";
import { SupportTicketActionList } from "src/modules/support/components/SupportTicketActionList";

import { useCreditReasonCodes } from "../hooks/useCreditReasonCodes";

interface CreditRequestsNotesSupportTabProps {
  history: H.History;
  isCreditRequests?: boolean;
  terminology: ITerminology;
  dropId?: number;
}

const defaultPagination: PaginatedParams<ISupportTicket> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "createdAt", "DESC" ] ]
};

/**
 * Support Tickets overview page
 */

const CreditRequestsNotesSupportTab: React.ReactFCWithChildren<CreditRequestsNotesSupportTabProps> = ({
  isCreditRequests, terminology, dropId
}) => {
  const TableRef = React.useRef<ITableInstance<ISupportTicket>>(null);
  const [ isLoading, setIsLoading ] = React.useState(false);
  const [ supportTickets, setSupportTickets ] = React.useState<PaginatedData<ISupportTicket>>();
  const [ listSupportTicketsRes, listSupportTicketsReq ] = useCreditRequestApi("LIST_CREDIT_REQUEST_SUPPORT_TICKETS");
  const [ exportCreditsRes, exportCreditsReq ] = useCreditRequestApi("EXPORT_CREDITS");
  const creditRequestReasons = useCreditReasonCodes();

  /**
   * Fetches table data
   */

  const onFetchData = React.useCallback((params: {filters: FilterParams<ISupportTicket>, pagination: PaginatedParams<ISupportTicket>}) => {
    const filters = params.filters;
    const pagination = params.pagination;

    // Set loading to true
    setIsLoading(true);

    if (dropId) {
      filters.branchId = dropId;
    }

    // Fetch support tickets
    listSupportTicketsReq({
      filters: isCreditRequests ? {
        ...filters,
        creditStatus: CreditStatus.RequiresReview
      } : filters,
      pagination,
      status: isCreditRequests ? JSON.stringify([ CreditRequestStatus.PendingApproval ]) : JSON.stringify([ CreditRequestStatus.Approved, CreditRequestStatus.PartiallyApproved ])
    });
  }, [
    dropId,
    isCreditRequests,
    listSupportTicketsReq
  ]);

  // listSupportTicketsRes change
  React.useEffect(() => {
    // If an error occurs show a toast
    if (listSupportTicketsRes.error) {
      setIsLoading(false);
      toast.error(listSupportTicketsRes.error.message);
    }

    // On success
    if (listSupportTicketsRes.data) {
      setIsLoading(false);
      setSupportTickets(listSupportTicketsRes.data);
    }
  }, [ listSupportTicketsRes ]);

  // listSupportTicketsRes change
  React.useEffect(() => {
    // If an error occurs show a toast
    if (exportCreditsRes.error) {
      toast.error(exportCreditsRes.error.message);
    }

    // On success
    if (exportCreditsRes.data) {
      toast.success("Your export is running, you will recieve an email with the export file attached shortly.");
    }
  }, [ exportCreditsRes ]);

  const columns = React.useMemo(() => {
    return getAdminCreditRequestTicketsColumns({
      actionButton: info => {
        return (
          <SupportTicketActionList
            ticketId={info.row.original.id}
            context={dropId ? "drop" : undefined}
          />
        );
      },
      terminology,
      creditRequestReasons,
      isCreditRequests

    });
  }, [
    terminology,
    creditRequestReasons,
    isCreditRequests,
    dropId
  ]);

  return (
    <>
      {!isCreditRequests && !dropId && (
        <Grid.Bounds halign="right">
          <Button
            loading={exportCreditsRes.pending}
            onClick={() => exportCreditsReq(null)}
          >
            Export
          </Button>
        </Grid.Bounds>
      )}

      <Table
        ref={TableRef}
        loading={isLoading}
        tableOf= {isCreditRequests ? "Credit Requests" : "Credit Notes"}
        defaultPagination={defaultPagination}
        data={supportTickets ? supportTickets.result : []}
        onFetchData={onFetchData}
        pageCount={supportTickets ? supportTickets.pages : 0}
        rowCount={supportTickets ? supportTickets.count : 0}
        columns={columns}
      />
    </>
  );
};

export default CreditRequestsNotesSupportTab;
